/**
 * THE BACKGROUND JOBS the app waits on — the one place that says what each
 * kind of queued work is called and what it makes stale.
 *
 * Keyed by the row's `eventType`; a row is recognised only when its `kind`
 * matches too. The analysis runs first and queues the other five when it
 * finishes, so their rows appear only after the analysis row reads DONE.
 *
 * `queriesToRefetch` are operation names: the ones on screen are re-asked the
 * moment the job finishes. `evict` are the root fields those operations read,
 * dropped from the cache so a screen opened LATER asks the server rather than
 * showing what it cached before the job ran.
 *
 * `watchMs` is how long the tracker waits for a job's row to appear once it is
 * expected, where the default half minute is too short.
 *
 * `verify` is for a job that can read DONE without having produced anything —
 * some do not report failures yet. It is asked after the job finishes, and an
 * empty answer is treated as a failure.
 *
 * Nothing else in the app knows about job types: the provider
 * (api/backgroundJobs.js) reads this map and screens ask it by subject.
 */
import { PORTFOLIO_REVIEW } from '../api/queries';

/** The analysis job's kind — what "is this portfolio being analysed" asks about. */
export const ANALYSIS_KIND = 'batch:portfolio_analytics';

/** The event a portfolio's style risk is computed and stored under. */
export const STYLE_RISK_RUN = 'fundamental_style_factor.run';

export const JOB_TYPES = {
  'portfolio.analytics.on_demand.run': {
    kind: ANALYSIS_KIND,
    label: 'Portfolio analysis',
    /* What it queues when it finishes. Their rows land a moment after it reads
       DONE — with nothing else running then, the tracker would stop polling
       and never see them, so it keeps watching for these. */
    queues: [
      'portfolio.review.run', 'client.review.run', 'client.brief.run',
      'action_items.generate', 'portfolio.gap_analysis.run', STYLE_RISK_RUN,
    ],
    /* Portfolio, holdings, risk, attribution, exposures and goal progress.
       Holdings, exposures and goal progress travel on getPortfolio. */
    queriesToRefetch: [
      'GetPortfolio', 'GetPortfolios', 'GetPortfolioBookRow', 'GetPortfolioGoal',
      'PortfolioValueHistory', 'PortfolioRiskHistory', 'PortfolioRiskTimeSeries',
      'HoldingWeightsHistory', 'FactorExposuresHistory', 'BehavioralRisk',
      'ReturnAttribution', 'ReturnAttributionWithIndex', 'BrinsonAttribution',
      'LatestBrinsonAttribution', 'GetPortfolioMultifactorAttribution',
      'GetGoals', 'GetActiveGoals',
      /* The Fundamental style and Regression exposures tabs, and the style
         snapshot other screens mount on their own. */
      'PortfolioFactorView', 'StyleFactorSnapshot',
    ],
    evict: [
      /* The style snapshot, and both sources of the holdings an opened style
         exposure lists — the card starts its holdings over when the snapshot
         it is showing is replaced. */
      'fundamentalStyleFactors', 'fundamentalStyleFactorAnalysis', 'fundamentalStyleFactorHoldingContributions',
      'getPortfolio', 'getPortfolios', 'getPortfolioValueHistory', 'getPortfolioRiskMetricsHistory',
      'getPortfolioHoldingWeightsHistory', 'getPortfolioFactorExposuresHistory', 'getBehavioralRisk',
      'getReturnAttribution', 'getBrinsonAttribution', 'getLatestBrinsonAttribution',
      'getMultifactorAttribution', 'getGoals', 'getActiveGoals',
    ],
  },
  /* Queued by the analysis (and by a statement import) once it reaches its
     queueing step: the portfolio's style risk, written a day at a time from
     its start date to today. The three reads behind "Risk by fundamental
     style" only return what is stored, so they are re-asked when it ends —
     and while it runs, by the card itself (see useStyleRiskJob). Its row is
     given two minutes to appear after the analysis ends before the run is
     read as never having been queued. */
  [STYLE_RISK_RUN]: {
    kind: 'event',
    label: 'Style risk',
    watchMs: 120000,
    queriesToRefetch: ['StyleFactorRisk', 'StyleFactorSnapshot', 'StyleRiskHistory', 'StyleRealizedRisk'],
    evict: ['fundamentalStyleFactorRisk', 'fundamentalStyleFactors', 'fundamentalStyleFactorRiskHistory'],
  },
  'portfolio.review.run': {
    kind: 'event',
    label: 'Portfolio review',
    queriesToRefetch: ['PortfolioReview', 'ReviewCommentary', 'GoalReviewCommentary', 'ReviewHistory'],
    evict: ['getPortfolioReview', 'getReviewCommentary', 'getGoalReviewCommentary', 'getReviewHistory'],
    /* Only the daily edition is written on demand. */
    verify: {
      query: PORTFOLIO_REVIEW,
      variables: (subject) => ({ portfolioSecuritySymbol: subject, window: 'daily' }),
      isEmpty: (data) => data?.getPortfolioReview == null,
    },
  },
  'client.review.run': {
    kind: 'event',
    label: 'Client review',
    queriesToRefetch: ['ClientReview'],
    evict: ['getClientReview'],
  },
  /* The app reads no client brief, gap analysis or action-item list of its
     own yet (open actions ride on the reviews), so these two refetch nothing
     until a screen does — add its operation here when one exists. */
  'client.brief.run': {
    kind: 'event',
    label: 'Client brief',
    queriesToRefetch: [],
    evict: ['getClientBrief'],
  },
  'action_items.generate': {
    kind: 'event',
    label: 'Action items',
    queriesToRefetch: ['ReviewOpenActions', 'GetGoals'],
    evict: ['getReviewOpenActions', 'getUserActionItems'],
  },
  'portfolio.gap_analysis.run': {
    kind: 'event',
    label: 'Gap analysis',
    queriesToRefetch: [],
    evict: ['getPortfolioGapAnalysis'],
  },
};

/** What an unrecognised row is shown as — tracked, never refetched for. */
const UNKNOWN_TYPE = {
  kind: null, label: 'Background job', queriesToRefetch: [], evict: [], queues: [],
};

/** The type a row belongs to, matched on kind AND event type. */
export const jobTypeOf = (row) => {
  const type = JOB_TYPES[row?.eventType];
  return type && type.kind === row.kind ? type : UNKNOWN_TYPE;
};

/** Whether a row is of `type` — a job kind ("batch:portfolio_analytics") or an event type. */
export const isOfType = (row, type) => !type || row.kind === type || row.eventType === type;
